/// A regra persistida que aponta o alvo provável de um pagamento do extrato. Ela sugere e nunca
/// concilia: o vínculo continua nascendo de uma confirmação humana (ADR 0021 e ADR 0024).
///
/// O casamento mora aqui, e não no módulo `finance`, porque a tela precisa dele: o testador avalia
/// a condição no browser a cada tecla e o servidor a reavalia ao gravar e ao sugerir. Duas cópias
/// divergiriam, e a tela passaria a prometer um resultado que a gravação recusa.

/// Recusa de validação da regra. O pacote não conhece o `ApplicationError` da API: o módulo que
/// chama traduz esta falha para o erro da sua camada.
export class RuleValidationError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = 'RuleValidationError';
  }
}

export const RECONCILIATION_RULE_MODES = ['SIMPLE', 'REGEX'] as const;
export type ReconciliationRuleMode = (typeof RECONCILIATION_RULE_MODES)[number];

export const RECONCILIATION_RULE_OPERATORS = [
  'CONTAINS',
  'STARTS_WITH',
  'ENDS_WITH',
  'EQUALS',
  'ANY_OF',
] as const;
export type ReconciliationRuleOperator = (typeof RECONCILIATION_RULE_OPERATORS)[number];

/// Duas famílias produzem obrigação que se repete: a despesa recorrente do próprio Financeiro e o
/// parcelamento publicado por outro módulo. A regra aponta para a família, nunca para a ocorrência
/// do mês — essa é descartável e nasce outra a cada competência.
export const RECONCILIATION_RULE_TARGET_KINDS = ['RECURRENCE', 'SOURCE_FAMILY'] as const;
export type ReconciliationRuleTargetKind = (typeof RECONCILIATION_RULE_TARGET_KINDS)[number];

export type ReconciliationRuleTarget =
  | { kind: 'RECURRENCE'; recurrenceRuleId: string }
  | { kind: 'SOURCE_FAMILY'; sourceKey: string; entity: string; externalId: string };

/// A identidade da família, do jeito que a regra a guarda. Ela não pode conter a ocorrência: é
/// justamente por apontar a família que a regra continua valendo no mês seguinte.
export function familyKeyOf(target: ReconciliationRuleTarget): string {
  return target.kind === 'RECURRENCE'
    ? `RECURRENCE:${target.recurrenceRuleId}`
    : `SOURCE_FAMILY:${target.sourceKey}:${target.entity}:${target.externalId}`;
}

const MAX_PATTERN_LENGTH = 600;
const MAX_TYPED_PATTERN_LENGTH = 200;
const MAX_REPEAT = 100;
const MAX_TERMS = 8;
const MAX_TERM_LENGTH = 60;
const MIN_TERM_LENGTH = 2;

function invalid(message: string): RuleValidationError {
  return new RuleValidationError(message);
}

/// Tirar o acento sem mexer na caixa: minúscula em uma expressão regular trocaria `\D` por `\d` e
/// inverteria o que a regra procura.
function stripDiacritics(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '');
}

/// O extrato escreve a mesma coisa de vários jeitos — "ENEL SÃO PAULO", "Enel Sao Paulo". Comparar
/// sem acento e em minúscula é o mínimo para uma regra escrita à mão continuar valendo. A troca
/// preserva a posição de cada caractere, e é isso que permite destacar o trecho encontrado.
export function normalizeForMatch(value: string): string {
  return stripDiacritics(value).toLowerCase();
}

function escapeLiteral(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/gu, (char) => `\\${char}`);
}

export function normalizeTerm(value: string): string {
  return value.replace(/\s+/gu, ' ').trim();
}

/// O modo simples existe para quem não conhece expressão regular: a pessoa escolhe a condição em
/// linguagem de negócio e o sistema escapa o texto, produzindo um padrão que não casa por acidente.
export function buildSimplePattern(
  operator: ReconciliationRuleOperator,
  terms: readonly string[],
): string {
  const cleaned = terms.map(normalizeTerm).filter((term) => term.length > 0);
  if (cleaned.length === 0) throw invalid('Informe o texto que a regra procura no extrato.');
  if (operator !== 'ANY_OF' && cleaned.length > 1) {
    throw invalid('Só "contém uma destas expressões" aceita mais de um texto.');
  }
  if (cleaned.length > MAX_TERMS) {
    throw invalid(`Use no máximo ${String(MAX_TERMS)} expressões em uma regra.`);
  }
  for (const term of cleaned) {
    if (term.length < MIN_TERM_LENGTH || term.length > MAX_TERM_LENGTH) {
      throw invalid(
        `Cada texto procurado deve ter de ${String(MIN_TERM_LENGTH)} a ${String(MAX_TERM_LENGTH)} caracteres.`,
      );
    }
  }
  const body = cleaned.map((term) => escapeLiteral(normalizeForMatch(term))).join('|');
  const group = cleaned.length > 1 ? `(?:${body})` : body;
  if (operator === 'STARTS_WITH') return `^${group}`;
  if (operator === 'ENDS_WITH') return `${group}$`;
  if (operator === 'EQUALS') return `^${group}$`;
  return group;
}

export function assertTypedPattern(pattern: string): string {
  const trimmed = pattern.trim();
  if (trimmed.length === 0) throw invalid('Escreva a expressão regular que a regra deve usar.');
  if (trimmed.length > MAX_TYPED_PATTERN_LENGTH) {
    throw invalid(
      `A expressão regular deve ter no máximo ${String(MAX_TYPED_PATTERN_LENGTH)} caracteres.`,
    );
  }
  compileRulePattern(trimmed);
  return trimmed;
}

type Position = 'NONE' | 'ATOM' | 'GROUP' | 'QUANTIFIER' | 'LAZY';

/// Só um subconjunto da sintaxe entra. O que fica de fora não é capricho: retrovisão e retrocesso
/// tornam o custo imprevisível, e um quantificador aplicado a um grupo é a receita conhecida de
/// travar o servidor com uma linha de extrato comprida.
export function assertSafePattern(pattern: string): void {
  if (pattern.length === 0) throw invalid('Escreva a expressão que a regra deve procurar.');
  if (pattern.length > MAX_PATTERN_LENGTH) throw invalid('Essa expressão é longa demais.');
  let depth = 0;
  let position: Position = 'NONE';
  let index = 0;
  while (index < pattern.length) {
    const char = pattern[index]!;
    if (char === '\\') {
      index = readEscape(pattern, index);
      position = 'ATOM';
      continue;
    }
    if (char === '(') {
      if (pattern.startsWith('(?', index) && !pattern.startsWith('(?:', index)) {
        throw invalid('Retrovisão e grupo nomeado não são aceitos em uma regra.');
      }
      depth += 1;
      index += pattern.startsWith('(?:', index) ? 3 : 1;
      position = 'NONE';
      continue;
    }
    if (char === ')') {
      if (depth === 0) throw invalid('Há um parêntese fechando sem abrir.');
      depth -= 1;
      index += 1;
      position = 'GROUP';
      continue;
    }
    if (char === '[') {
      index = readClass(pattern, index);
      position = 'ATOM';
      continue;
    }
    if (char === ']') throw invalid('Há um colchete fechando sem abrir.');
    if (char === '*' || char === '+') {
      requireAtom(position);
      index += 1;
      position = 'QUANTIFIER';
      continue;
    }
    if (char === '?') {
      if (position !== 'ATOM' && position !== 'QUANTIFIER') requireAtom(position);
      index += 1;
      position = position === 'QUANTIFIER' ? 'LAZY' : 'QUANTIFIER';
      continue;
    }
    if (char === '{') {
      requireAtom(position);
      index = readRepeat(pattern, index);
      position = 'QUANTIFIER';
      continue;
    }
    if (char === '}') throw invalid('Há uma chave fechando sem abrir.');
    index += 1;
    position = char === '|' || char === '^' || char === '$' ? 'NONE' : 'ATOM';
  }
  if (depth !== 0) throw invalid('Há um parêntese aberto sem fechar.');
}

function requireAtom(position: Position): void {
  if (position === 'ATOM') return;
  if (position === 'GROUP') {
    throw invalid('Repetição depois de um grupo não é aceita: repita um caractere ou uma classe.');
  }
  throw invalid('Há uma repetição sem nada para repetir.');
}

const ESCAPABLE = new Set([
  '^',
  '$',
  '\\',
  '.',
  '*',
  '+',
  '?',
  '(',
  ')',
  '[',
  ']',
  '{',
  '}',
  '|',
  '/',
]);
const ESCAPE_CLASSES = new Set(['d', 'D', 'w', 'W', 's', 'S', 'b', 'B']);

function readEscape(pattern: string, index: number): number {
  const char = pattern[index + 1];
  if (char === undefined) throw invalid('A expressão termina com uma barra invertida solta.');
  if (/[0-9]/u.test(char)) throw invalid('Referência a grupo anterior não é aceita em uma regra.');
  if (!ESCAPABLE.has(char) && !ESCAPE_CLASSES.has(char)) {
    throw invalid(`A sequência "\\${char}" não é aceita em uma regra.`);
  }
  return index + 2;
}

function readClass(pattern: string, index: number): number {
  let cursor = index + 1;
  if (pattern[cursor] === '^') cursor += 1;
  if (pattern[cursor] === ']') cursor += 1;
  while (cursor < pattern.length) {
    const char = pattern[cursor]!;
    if (char === '\\') {
      if (pattern[cursor + 1] === undefined) break;
      cursor += 2;
      continue;
    }
    if (char === ']') return cursor + 1;
    cursor += 1;
  }
  throw invalid('Há um colchete aberto sem fechar.');
}

function readRepeat(pattern: string, index: number): number {
  const end = pattern.indexOf('}', index);
  if (end === -1) throw invalid('Há uma chave aberta sem fechar.');
  const body = pattern.slice(index + 1, end);
  const parsed = /^(\d{1,3})(,(\d{1,3})?)?$/u.exec(body);
  if (!parsed) throw invalid('Repetição deve ser escrita como {2}, {2,} ou {2,5}.');
  const minimum = Number(parsed[1]);
  const maximum =
    parsed[3] === undefined ? (parsed[2] === undefined ? minimum : MAX_REPEAT) : Number(parsed[3]);
  if (minimum > maximum) throw invalid('Na repetição, o mínimo não pode ser maior que o máximo.');
  if (maximum > MAX_REPEAT) {
    throw invalid(`Repetição de no máximo ${String(MAX_REPEAT)} vezes em uma regra.`);
  }
  return end + 1;
}

export function compileRulePattern(pattern: string): RegExp {
  assertSafePattern(pattern);
  let regex: RegExp;
  try {
    regex = new RegExp(stripDiacritics(pattern), 'iu');
  } catch {
    throw invalid('Essa expressão não é uma expressão regular válida.');
  }
  // Um padrão que casa com texto vazio casa com tudo, e uma regra assim tomaria para si qualquer
  // linha do extrato.
  if (regex.test('')) {
    throw invalid('Essa expressão casa até com uma linha vazia. Torne-a mais específica.');
  }
  return regex;
}

export interface RuleCondition {
  pattern: string;
  direction: 'INFLOW' | 'OUTFLOW' | null;
  financeAccountId: string | null;
  minAmountCents: number | null;
  maxAmountCents: number | null;
}

export interface RuleSubject {
  description: string;
  direction: 'INFLOW' | 'OUTFLOW';
  financeAccountId: string;
  amountCents: number;
}

/// Onde o trecho foi encontrado na descrição original. O testador destaca exatamente isso.
export interface RuleHit {
  start: number;
  end: number;
}

export function assertAmountRange(
  minAmountCents: number | null,
  maxAmountCents: number | null,
): void {
  for (const value of [minAmountCents, maxAmountCents]) {
    if (value !== null && (!Number.isSafeInteger(value) || value <= 0)) {
      throw invalid('A faixa de valor aceita apenas valores positivos.');
    }
  }
  if (minAmountCents !== null && maxAmountCents !== null && minAmountCents > maxAmountCents) {
    throw invalid('Na faixa de valor, o mínimo não pode ser maior que o máximo.');
  }
}

export function ruleHit(
  rule: RuleCondition,
  subject: RuleSubject,
  compiled?: RegExp,
): RuleHit | null {
  if (rule.direction !== null && rule.direction !== subject.direction) return null;
  if (rule.financeAccountId !== null && rule.financeAccountId !== subject.financeAccountId) {
    return null;
  }
  if (rule.minAmountCents !== null && subject.amountCents < rule.minAmountCents) return null;
  if (rule.maxAmountCents !== null && subject.amountCents > rule.maxAmountCents) return null;
  const regex = compiled ?? compileRulePattern(rule.pattern);
  const found = regex.exec(normalizeForMatch(subject.description));
  return found === null ? null : { start: found.index, end: found.index + found[0].length };
}

/// Palavras que aparecem em qualquer linha de extrato. Uma regra criada a partir delas casaria
/// pagamentos que nada têm a ver entre si.
const GENERIC_WORDS = new Set([
  'banco',
  'boleto',
  'cobranca',
  'compra',
  'conta',
  'credito',
  'debito',
  'deposito',
  'fatura',
  'mensal',
  'pagamento',
  'pagto',
  'parcela',
  'recebimento',
  'saque',
  'tarifa',
  'transferencia',
  'valor',
]);
const MAX_SUGGESTED_TERMS = 3;

/// O texto que a regra proposta procura, tirado do pagamento que a pessoa acabou de conciliar. É o
/// trecho contíguo mais longo de palavras próprias da linha, sem número, data nem palavra genérica.
export function suggestTermFrom(description: string): string | null {
  const words = normalizeForMatch(description).split(/[^a-z]+/u);
  let best: string[] = [];
  let current: string[] = [];
  for (const word of words) {
    if (word.length >= 3 && !GENERIC_WORDS.has(word)) {
      current.push(word);
      if (current.length > MAX_SUGGESTED_TERMS) current.shift();
      if (current.join(' ').length > best.join(' ').length) best = [...current];
      continue;
    }
    current = [];
  }
  return best.length === 0 ? null : best.join(' ');
}

/// Um padrão gravado já passou pela validação, mas uma regra defeituosa não pode derrubar a lista
/// inteira: ela apenas deixa de sugerir.
export function safeRegex(pattern: string): RegExp | null {
  try {
    return compileRulePattern(pattern);
  } catch {
    return null;
  }
}

export interface RuleMatcher {
  id: string;
  name: string;
  targetLabel: string;
  condition: RuleCondition;
}

export interface PreparedRule extends RuleMatcher {
  regex: RegExp | null;
}

/// Compilar uma vez e reaproveitar: o testador reavalia a fila inteira a cada tecla, e recompilar
/// a expressão a cada linha do extrato desperdiçaria o trabalho.
export function prepareRule(rule: RuleMatcher): PreparedRule {
  return { ...rule, regex: safeRegex(rule.condition.pattern) };
}

export interface RuleTrialOutcome {
  ruleId: string | null;
  ruleName: string | null;
  targetLabel: string | null;
  start: number | null;
  end: number | null;
  /// As demais regras que também casaram. É o que torna o conflito visível sem esconder quem venceu.
  conflicts: string[];
  /// Toda regra que casou, do vencedor para os demais. É por aqui que o testador sabe se a regra em
  /// edição casou mesmo quando outra vem antes na fila e leva o movimento.
  hitIds: string[];
}

/// Quem a fila escolheria para esse texto. `subject` nulo é o exemplo digitado à mão: ele não tem
/// conta nem valor, então só a condição de texto é avaliada — cada regra é medida contra um sujeito
/// que satisfaz os próprios filtros dela.
export function trialOf(
  queue: readonly PreparedRule[],
  text: string,
  subject: RuleSubject | null,
): RuleTrialOutcome {
  const hits: { rule: PreparedRule; start: number; end: number }[] = [];
  for (const rule of queue) {
    if (rule.regex === null) continue;
    const against: RuleSubject = subject ?? {
      description: text,
      direction: rule.condition.direction ?? 'OUTFLOW',
      financeAccountId: rule.condition.financeAccountId ?? '',
      amountCents: rule.condition.minAmountCents ?? 1,
    };
    const hit = ruleHit(rule.condition, against, rule.regex);
    if (hit) hits.push({ rule, ...hit });
  }
  const winner = hits[0];
  return {
    ruleId: winner?.rule.id ?? null,
    ruleName: winner?.rule.name ?? null,
    targetLabel: winner?.rule.targetLabel ?? null,
    start: winner?.start ?? null,
    end: winner?.end ?? null,
    conflicts: hits.slice(1).map((hit) => hit.rule.name),
    hitIds: hits.map((hit) => hit.rule.id),
  };
}
